// IDI-649 D1/D3: prepare an uploaded logo so it fits the dealer's settings
// record. Shared with onboarding's Your Business step (IDI-656).
//
// A PNG/JPEG within 900×300 px and ≤100 KB is kept exactly as uploaded.
// Any other readable image (SVG included) is drawn to fit 900×300 (never
// enlarged): PNG if ≤100 KB, else JPEG on white at the first of
// 0.9/0.8/0.7/0.6 that is ≤100 KB. Otherwise the upload is refused.

const LOGO_MAX_W = 900;
const LOGO_MAX_H = 300;
const LOGO_MAX_STORED_BYTES = 100 * 1024; // 102,400 bytes of image
const LOGO_JPEG_QUALITIES = [0.9, 0.8, 0.7, 0.6];
export const LOGO_RESIZE_FAILED = "This logo couldn't be resized to fit. Try a PNG or JPG under 100 KB.";

const KEEP_AS_IS_TYPES = ['image/png', 'image/jpeg'];

// Decoded byte length of a base64 data URL's payload.
export function dataUrlBytes(dataUrl) {
  const s = String(dataUrl || '');
  const comma = s.indexOf(',');
  if (comma < 0) return Infinity;
  const b64 = s.slice(comma + 1);
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - pad;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target?.result || '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('load failed'));
    img.src = src;
  });
}

const fail = () => ({ ok: false, message: LOGO_RESIZE_FAILED });

export async function prepareLogo(file) {
  try {
    const original = await readAsDataUrl(file);
    const img = await loadImage(original);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return fail();

    const meta = (dataUrl, type) => ({ filename: file.name, size: dataUrlBytes(dataUrl), type });

    if (KEEP_AS_IS_TYPES.includes(file.type) && w <= LOGO_MAX_W && h <= LOGO_MAX_H
        && file.size <= LOGO_MAX_STORED_BYTES) {
      return { ok: true, dataUrl: original, meta: { filename: file.name, size: file.size, type: file.type } };
    }

    const scale = Math.min(1, LOGO_MAX_W / w, LOGO_MAX_H / h);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return fail();

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = canvas.toDataURL('image/png');
    if (png.startsWith('data:image/png') && dataUrlBytes(png) <= LOGO_MAX_STORED_BYTES) {
      return { ok: true, dataUrl: png, meta: meta(png, 'image/png') };
    }

    ctx.fillStyle = 'white'; // JPEG has no alpha: paint on white (D1)
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of LOGO_JPEG_QUALITIES) {
      const jpeg = canvas.toDataURL('image/jpeg', q);
      if (jpeg.startsWith('data:image/jpeg') && dataUrlBytes(jpeg) <= LOGO_MAX_STORED_BYTES) {
        return { ok: true, dataUrl: jpeg, meta: meta(jpeg, 'image/jpeg') };
      }
    }
    return fail();
  } catch (err) {
    return fail();
  }
}

export default prepareLogo;
